import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Eraser, PenNib, Plus, Trash } from "@phosphor-icons/react";
import { api, schick } from "../api/client";
import type { Unterschriftsfeld } from "../api/types";
import { datum } from "../lib/format";
import { Dialog, Meldung } from "./Bausteine";

/**
 * Unterschriften bei Abnahme und Zustandsfeststellung (SPEC §5.6). Je Feld: Rolle, Name in
 * Druckschrift, Namenszug. Unterschrieben wird auf dem Tablet der Objektüberwachung —
 * der Auftragnehmer braucht dafür kein Konto.
 */
export function Unterschriften({ begehungId, felder, offen, geaendert }: {
  begehungId: string; felder: Unterschriftsfeld[]; offen: boolean; geaendert: () => void;
}) {
  const [zeichnen, setZeichnen] = useState<Unterschriftsfeld | null>(null);
  const [rolle, setRolle] = useState("");
  const qc = useQueryClient();
  const neu = useMutation({
    mutationFn: () => schick(`/begehungen/${begehungId}/unterschriften`, { rolle }),
    onSuccess: () => { setRolle(""); geaendert(); },
  });
  const weg = useMutation({
    mutationFn: ({ id, nurBild }: { id: string; nurBild: boolean }) => api("DELETE", `/unterschriften/${id}${nurBild ? "/bild" : ""}`),
    onSuccess: () => { geaendert(); qc.invalidateQueries({ queryKey: ["begehung", begehungId] }); },
  });
  return (
    <>
      <div className="unterschrift-felder">
        {felder.map((u) => (
          <div key={u.id} className="unterschrift-feld">
            <div className="mute">{u.rolle}</div>
            {u.bild_datei_id
              ? <img src={`/api/unterschriften/${u.id}/bild?v=${u.am}`} alt={`Unterschrift ${u.name}`} />
              : <div className="unterschrift-leer">noch nicht unterschrieben</div>}
            <div className="unterschrift-name">{u.name || " "}{u.am && <span className="mute"> · {datum(u.am)}</span>}</div>
            {offen && (
              <div className="punkt-leiste">
                <button className={`knopf${u.bild_datei_id ? "" : " haupt"}`} onClick={() => setZeichnen(u)}>
                  <PenNib size={16} /> {u.bild_datei_id ? "Neu unterschreiben" : "Unterschreiben"}
                </button>
                {u.bild_datei_id && <button className="knopf leise" onClick={() => weg.mutate({ id: u.id, nurBild: true })}>
                  <Eraser size={16} /> Löschen</button>}
                {!u.bild_datei_id && felder.length > 1 && (
                  <button className="knopf leise symbol gefahr" aria-label="Feld entfernen"
                          onClick={() => weg.mutate({ id: u.id, nurBild: false })}><Trash size={16} /></button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {offen && (
        <form className="abschnitt-neu" onSubmit={(e) => { e.preventDefault(); if (rolle.trim()) neu.mutate(); }}>
          <input className="eingabe" placeholder="Weiteres Feld, z. B. Für den Nutzer" value={rolle} onChange={(e) => setRolle(e.target.value)} />
          <button className="knopf" disabled={!rolle.trim()}><Plus size={15} /> Feld</button>
        </form>
      )}
      <Meldung fehler={neu.error ?? weg.error} />
      {zeichnen && <Zeichenfeld feld={zeichnen} zu={() => setZeichnen(null)} fertig={() => { setZeichnen(null); geaendert(); }} />}
    </>
  );
}

/**
 * Das Zeichenfeld. Pointer Events statt Maus/Touch getrennt: Finger, Stift und Maus gehen
 * gleich, `touch-action: none` hält den Finger vom Scrollen ab. Die Leinwand ist doppelt
 * so fein wie angezeigt (devicePixelRatio), sonst wird der Namenszug im PDF pixelig.
 */
function Zeichenfeld({ feld, zu, fertig }: { feld: Unterschriftsfeld; zu: () => void; fertig: () => void }) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const [name, setName] = useState(feld.name);
  const [leer, setLeer] = useState(true);
  const zug = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const c = leinwand.current!;
    const f = Math.max(2, window.devicePixelRatio || 1);
    c.width = c.clientWidth * f;
    c.height = c.clientHeight * f;
    const g = c.getContext("2d")!;
    g.scale(f, f);
    g.lineCap = "round";
    g.lineJoin = "round";
    g.strokeStyle = "#1b2a4a";
    g.lineWidth = 2.4;
  }, []);

  const punkt = (e: React.PointerEvent) => {
    const r = leinwand.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const speichern = useMutation({
    mutationFn: () => api("PUT", `/unterschriften/${feld.id}`, { name, bild: zuschneiden(leinwand.current!) }),
    onSuccess: fertig,
  });

  return (
    <Dialog offen zu={zu} titel={`Unterschrift – ${feld.rolle}`} fuss={<>
      <button className="knopf leise" onClick={() => {
        const c = leinwand.current!;
        c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
        setLeer(true);
      }}><Eraser size={16} /> Neu</button>
      <div style={{ flex: 1 }} />
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={leer || !name.trim() || speichern.isPending} onClick={() => speichern.mutate()}>Übernehmen</button>
    </>}>
      <label className="feld"><span>Name in Druckschrift</span>
        <input className="eingabe" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></label>
      <canvas ref={leinwand} className="zeichenfeld"
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); zug.current = punkt(e); }}
              onPointerMove={(e) => {
                if (!zug.current) return;
                const g = leinwand.current!.getContext("2d")!;
                const p = punkt(e);
                g.beginPath(); g.moveTo(zug.current.x, zug.current.y); g.lineTo(p.x, p.y); g.stroke();
                zug.current = p;
                setLeer(false);
              }}
              onPointerUp={() => { zug.current = null; }} onPointerCancel={() => { zug.current = null; }} />
      <div className="mute">Mit Finger oder Stift im Feld unterschreiben.</div>
      <Meldung fehler={speichern.error} />
    </Dialog>
  );
}

/** Nur den beschriebenen Bereich übernehmen (plus Rand) — kleinere Datei, und im PDF steht
 *  der Namenszug nicht verloren in einer großen leeren Fläche. */
function zuschneiden(c: HTMLCanvasElement): string {
  const g = c.getContext("2d")!;
  const { data, width, height } = g.getImageData(0, 0, c.width, c.height);
  let x0 = width, y0 = height, x1 = 0, y1 = 0;
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      if (data[(y * width + x) * 4 + 3]! > 0) {
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  if (x1 <= x0 || y1 <= y0) return c.toDataURL("image/png");
  const rand = 12;
  x0 = Math.max(0, x0 - rand); y0 = Math.max(0, y0 - rand);
  x1 = Math.min(width, x1 + rand); y1 = Math.min(height, y1 + rand);
  const z = document.createElement("canvas");
  z.width = x1 - x0; z.height = y1 - y0;
  z.getContext("2d")!.drawImage(c, x0, y0, z.width, z.height, 0, 0, z.width, z.height);
  return z.toDataURL("image/png");
}
